import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import PropTypes from 'prop-types';

import { OTP_LENGTH } from '../forgot-password-code/data/constants';

const VerificationCodeInput = forwardRef(({
  length,
  isInvalid,
  isSuccess,
  onComplete,
  className,
}, ref) => {
  const [code, setCode] = useState(new Array(length).fill(''));
  const inputsRef = useRef([]);

  useImperativeHandle(ref, () => ({
    reset() {
      setCode(new Array(length).fill(''));
    },
  }));

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) {
      return;
    }

    const newCode = [...code];

    if (value) {
      // Place value in the first empty slot
      for (let i = 0; i < length; i++) {
        if (newCode[i] === '') {
          newCode[i] = value;
          break;
        }
      }
      setCode(newCode);

      // Move to next empty input
      const nextEmptyIndex = newCode.indexOf('');
      if (nextEmptyIndex !== -1) {
        inputsRef.current[nextEmptyIndex].focus();
      }
    }

    // If all fields are filled, trigger onComplete
    if (newCode.every((digit) => digit !== '') && onComplete) {
      onComplete(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newCode = [...code];

      // Find last filled index from the right
      for (let i = length - 1; i >= 0; i--) {
        if (newCode[i] !== '') {
          newCode[i] = '';
          setCode(newCode);
          inputsRef.current[i].focus();
          break;
        }
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').trim().slice(0, length);

    if (/^\d+$/.test(pastedText)) {
      const newCode = pastedText.split('').concat(new Array(length - pastedText.length).fill(''));
      setCode(newCode);

      // Move focus to the last entered digit
      const nextEmptyIndex = newCode.indexOf('');
      inputsRef.current[nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex].focus();

      if (newCode.every((digit) => digit !== '') && onComplete) {
        onComplete(newCode.join(''));
      }
    }
  };

  return (
    <div className={`verification-code-input d-flex ${className || ''}`} dir="ltr">
      {code.map((digit, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          // eslint-disable-next-line no-return-assign
          ref={(el) => (inputsRef.current[index] = el)}
          type="text"
          name={`verification-code-${index}`}
          maxLength="1"
          value={digit}
          disabled={isSuccess}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className={`verification-code-box ${isInvalid ? 'invalid-box' : ''} ${isSuccess ? 'success-box' : ''}`}
        />
      ))}
    </div>
  );
});

VerificationCodeInput.propTypes = {
  length: PropTypes.number,
  isSuccess: PropTypes.bool,
  isInvalid: PropTypes.bool,
  onComplete: PropTypes.func.isRequired,
  className: PropTypes.string,
};

VerificationCodeInput.defaultProps = {
  length: OTP_LENGTH,
  isSuccess: false,
  isInvalid: false,
};

export default VerificationCodeInput;
